<template>
  <div>
    <div class="body1 mb-6" data-testid="allow-block-list-subtitle">{{ $t(subtitleKey) }}</div>

    <button-toggle-group
      v-if="tabs.length > 1"
      :model-value="activeTab"
      :items="tabs"
      class="mb-8 w-100 original-text"
      @update:model-value="onTabChange"
    />

    <v-form ref="form" v-model="valid" @submit.prevent>
      <allow-block-terms-field
        v-if="activeTab === AllowBlockListTab.EMAILS_DOMAINS"
        v-model="emailDomainItems"
        data-testid="add-user-modal-combobox"
        :label="emailDomainFieldLabel"
        :rules="emailDomainRules"
        :error-messages="errorMessages"
        :invalid-terms="invalidTerms"
        :is-valid-term="isEmailOrDomainValid"
      />

      <allow-block-terms-field
        v-else-if="activeTab === AllowBlockListTab.IPS_IP_RANGES"
        v-model="ipItems"
        data-testid="add-ip-modal-combobox"
        :label="ipFieldLabel"
        :rules="ipRules"
        :error-messages="errorMessages"
        :invalid-terms="invalidTerms"
        :is-valid-term="isIpValid"
      />

      <allow-block-terms-field
        v-else-if="countryEnabled && activeTab === AllowBlockListTab.COUNTRIES"
        v-model="countryItems"
        data-testid="add-country-modal-autocomplete"
        :field-type="AllowBlockTermsFieldType.OPTIONS"
        :items="recognizedCountries"
        :label="countryFieldLabel"
        :error-messages="errorMessages"
        :invalid-terms="invalidTerms"
        :is-valid-term="isCountryCode"
      />

      <allow-block-tld-field
        v-else-if="tldEnabled && activeTab === AllowBlockListTab.TLDS"
        v-model="tldItems"
        data-testid="add-tld-modal-autocomplete"
        :items="tldOptions"
        :label="tldFieldLabel"
        :loading="tldsLoading"
        :error-messages="errorMessages"
        :invalid-terms="invalidTerms"
      />

      <v-text-field
        v-model="localDescription"
        class="mt-8"
        data-testid="add-to-allow-block-list-description-field"
        :label="$t('modals.addToAllowlist.descriptionLabel')"
        :placeholder="$t('modals.addToAllowlist.descriptionPlaceholder')"
        variant="outlined"
        maxlength="500"
        counter="500"
      />
    </v-form>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import type { Ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  AllowBlockListTab,
  AllowBlockTermsFieldType,
  EmailSecurityAllowBlockItemType,
  PhishingAction,
  UNSUPPORTED_GEO_BLOCK_COUNTRIES,
} from "@/constants/phishing";
import { countriesList } from "@/constants/countries";
import isEqual from "lodash/isEqual";
import { hasSeparator, Patterns } from "@coronet/cui";
import { uniq } from "lodash";
import type { VuetifyFormRef } from "@/types";
import type {
  ValidateAllowBlockTermsPayload,
  ValidateAllowBlockTermsResponse,
} from "@/_store/email-security/email-settings.module";
import { getIpv4MappedAddress, isReservedIP } from "@/_helpers/validators.ts";
import ButtonToggleGroup from "@/components/ButtonToggleGroup.vue";
import type { ButtonToggleItem } from "@/components/ButtonToggleGroup.vue";
import AllowBlockTermsField from "@/components/modals/email-settings/AllowBlockTermsField.vue";
import AllowBlockTldField from "@/components/modals/email-settings/AllowBlockTldField.vue";
import {
  buildTldFieldOptions,
  getAllowBlockListItemDisplayName,
  type TldFieldOption,
} from "@/_helpers/allow-block-list";

type ValidateTermsFn = (
  payload: ValidateAllowBlockTermsPayload
) => Promise<ValidateAllowBlockTermsResponse>;

export default defineComponent({
  components: { AllowBlockTermsField, AllowBlockTldField, ButtonToggleGroup },
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["update:valid", "update:localValue"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const form = ref<VuetifyFormRef>();
    const valid = ref(true);

    const activeTab = ref<AllowBlockListTab>(AllowBlockListTab.EMAILS_DOMAINS);
    const emailDomainItems: Ref<string[]> = ref([]);
    const ipItems: Ref<string[]> = ref([]);
    const countryItems: Ref<string[]> = ref([]);
    const tldItems: Ref<string[]> = ref([]);
    const localDescription = ref<string | undefined>(undefined);

    const tldOptions: Ref<TldFieldOption[]> = ref([]);
    const tldsLoading = ref(false);
    const tldsLoaded = ref(false);

    const invalidTerms: Ref<string[]> = ref([]);
    const serverErrorMessage = ref("");
    const requiredError = ref(false);

    const isAllowList = computed(() => !!props.config.item?.allowList);

    // Countries are available on blocklist only and not on the global list.
    const countryEnabled = computed(
      () =>
        props.config.action === PhishingAction.ADD_TO_BLOCKLIST &&
        !props.config.item?.disableCountry
    );

    const tldEnabled = computed(() => props.config.action === PhishingAction.ADD_TO_BLOCKLIST);

    const recognizedCountries = Object.entries(countriesList)
      .filter(([code]) => !UNSUPPORTED_GEO_BLOCK_COUNTRIES.includes(code))
      .map(([code, name]) => ({ title: name, value: code }));
    const recognizedCountryCodes = new Set(recognizedCountries.map(({ value }) => value));

    const isCountryCode = (value: string) => recognizedCountryCodes.has(value.toUpperCase());

    const tabs = computed<ButtonToggleItem<AllowBlockListTab>[]>(() => {
      const items: ButtonToggleItem<AllowBlockListTab>[] = [
        {
          value: AllowBlockListTab.EMAILS_DOMAINS,
          label: i18n.t("modals.allowBlockList.tabs.emailsOrDomains"),
        },
        {
          value: AllowBlockListTab.IPS_IP_RANGES,
          label: i18n.t("modals.allowBlockList.tabs.ipsIpRanges"),
        },
      ];
      if (countryEnabled.value) {
        items.push({
          value: AllowBlockListTab.COUNTRIES,
          label: i18n.t("modals.allowBlockList.tabs.countries"),
        });
      }
      if (tldEnabled.value) {
        items.push({
          value: AllowBlockListTab.TLDS,
          label: i18n.t("modals.allowBlockList.tabs.tlds"),
        });
      }
      return items;
    });

    const subtitleKey = computed(() => {
      if (isAllowList.value) {
        return "modals.addToAllowlist.subtitle";
      }
      return countryEnabled.value
        ? "modals.addToBlocklist.subtitle"
        : "modals.addToBlocklist.subtitleNoCountries";
    });

    const emailDomainFieldLabel = computed(() =>
      emailDomainItems.value.length
        ? i18n.t("modals.allowBlockList.emailsOrDomains.label")
        : i18n.t("modals.allowBlockList.emailsOrDomains.placeholder")
    );
    const ipFieldLabel = computed(() =>
      ipItems.value.length
        ? i18n.t("modals.allowBlockList.ipsIpRanges.label")
        : i18n.t("modals.allowBlockList.ipsIpRanges.placeholder")
    );
    const countryFieldLabel = computed(() =>
      countryItems.value.length
        ? i18n.t("modals.allowBlockList.countries.label")
        : i18n.t("modals.allowBlockList.countries.placeholder")
    );
    const tldFieldLabel = computed(() =>
      tldItems.value.length
        ? i18n.t("modals.allowBlockList.tlds.label")
        : i18n.t("modals.allowBlockList.tlds.placeholder")
    );

    // We submit only the active tab
    const activeItems = computed<string[]>(() => {
      if (activeTab.value === AllowBlockListTab.IPS_IP_RANGES) return ipItems.value;
      if (activeTab.value === AllowBlockListTab.COUNTRIES) return countryItems.value;
      if (activeTab.value === AllowBlockListTab.TLDS) return tldItems.value;
      return emailDomainItems.value;
    });

    const loadTldOptions = async () => {
      if (tldsLoaded.value || tldsLoading.value) {
        return;
      }

      tldsLoading.value = true;

      const fetchExistingTlds = props.config.item?.fetchExistingTlds;
      const fetchRestrictedTlds = props.config.item?.fetchRestrictedTlds;

      try {
        const [existingTlds, restrictedTlds] = await Promise.all([
          fetchExistingTlds?.() ?? [],
          fetchRestrictedTlds?.() ?? [],
        ]);

        if (restrictedTlds) {
          tldOptions.value = buildTldFieldOptions({
            restrictedTlds,
            existingTlds: existingTlds ?? [],
          });
          tldsLoaded.value = true;
        } else {
          serverErrorMessage.value = i18n.t("modals.allowBlockList.tlds.loadFailed");
        }
      } finally {
        tldsLoading.value = false;
      }
    };

    const isEmailOrDomainValid = (value: string) =>
      Patterns.EMAIL.test(value) || Patterns.WILDCARD_DOMAIN.test(value);
    const isIpFormatValid = (value: string) =>
      Patterns.IP.test(value) || getIpv4MappedAddress(value) !== null;
    const isIpValid = (value: string) =>
      isIpFormatValid(value) && !(!isAllowList.value && isReservedIP(value));

    const emailDomainRules = [
      (values: string[]) => {
        for (const value of values) {
          if (!isEmailOrDomainValid(value)) {
            return i18n.t("validations.emailOrDomain");
          }
        }
        return true;
      },
    ];

    const ipRules = [
      (values: string[]) => {
        for (const value of values) {
          if (!isIpFormatValid(value)) {
            return i18n.t("validations.ipOrCidrRange");
          }
          if (!isAllowList.value && isReservedIP(value)) {
            return i18n.t("validations.reservedIpNotAllowed");
          }
        }
        return true;
      },
    ];

    // Errors always belong to the tab the user is on: it is the only one holding
    // data, and switching tabs clears them.
    const errorMessages = computed(() => {
      const messages: string[] = [];
      if (requiredError.value) {
        messages.push(i18n.t("validations.required"));
      }
      if (serverErrorMessage.value) {
        messages.push(serverErrorMessage.value);
      }
      return messages;
    });

    const clearServerValidation = () => {
      invalidTerms.value = [];
      serverErrorMessage.value = "";
      requiredError.value = false;
    };

    const onTabChange = (tab: AllowBlockListTab) => {
      if (tab === activeTab.value) {
        return;
      }
      emailDomainItems.value = [];
      ipItems.value = [];
      countryItems.value = [];
      tldItems.value = [];
      localDescription.value = undefined;
      clearServerValidation();
      activeTab.value = tab;

      if (tab === AllowBlockListTab.TLDS) {
        loadTldOptions();
      }
    };

    const normalizeItems = (itemsRef: Ref<string[]>) => {
      const values = itemsRef.value;
      const last = values.at(-1);

      const expanded =
        last && hasSeparator(last) ? [...values.slice(0, -1), ...last.split(",")] : values;
      const normalized = uniq(expanded.map((item) => item.toLowerCase().trim()).filter(Boolean));

      if (!isEqual(normalized, values)) {
        itemsRef.value = normalized;
      }
    };

    watch(emailDomainItems, () => {
      clearServerValidation();
      normalizeItems(emailDomainItems);
    });
    watch(ipItems, () => {
      clearServerValidation();
      normalizeItems(ipItems);
    });
    // Picked from a fixed list, so nothing to normalize.
    watch([countryItems, tldItems], () => {
      clearServerValidation();
    });

    watch(
      [activeItems, localDescription],
      () => {
        emit("update:localValue", {
          items: activeItems.value,
          action: props.config.action,
          errors: [],
          description: localDescription.value,
        });
      },
      { deep: true }
    );

    watch(valid, (value) => {
      if (value === null) {
        return;
      }
      emit("update:valid", value);
    });

    // show readable country name
    const termDisplayName = (term: string) =>
      getAllowBlockListItemDisplayName({
        name: term,
        type:
          activeTab.value === AllowBlockListTab.COUNTRIES
            ? EmailSecurityAllowBlockItemType.COUNTRY
            : EmailSecurityAllowBlockItemType.DOMAIN,
      });

    // The backend only tells us which terms are duplicates
    const duplicateMessage = (terms: string[]) =>
      i18n.t(
        isAllowList.value
          ? "modals.allowBlockList.duplicates.allowlist"
          : "modals.allowBlockList.duplicates.blocklist",
        { term: termDisplayName(terms[0]), count: terms.length },
        terms.length
      );

    const validate = async (): Promise<boolean> => {
      clearServerValidation();

      // required validation
      const items = activeItems.value;
      if (!items.length) {
        requiredError.value = true;
        return false;
      }

      // rules validation
      const clientResult = await form.value?.validate();
      if (!clientResult?.valid) {
        return false;
      }

      // BE term validation
      const validateTerms = props.config.item?.validate as ValidateTermsFn | undefined;
      if (!validateTerms) {
        return true;
      }

      try {
        const {
          invalidTerms: failedTerms,
          duplicateTerms,
          errorMessage,
        } = await validateTerms({
          items,
          allowList: isAllowList.value,
        });
        if (failedTerms?.length) {
          invalidTerms.value = failedTerms;
          // Mixed failures keep the backend wording: it is the only one that can
          // explain the terms that are not duplicates
          const duplicates = new Set(duplicateTerms ?? []);
          const allDuplicates =
            duplicates.size > 0 && failedTerms.every((term) => duplicates.has(term));
          serverErrorMessage.value = allDuplicates ? duplicateMessage(failedTerms) : errorMessage;
          return false;
        }
        return true;
      } catch (error) {
        console.error("Failed to validate allow/block terms:", error);
        return false;
      }
    };

    return {
      AllowBlockListTab,
      AllowBlockTermsFieldType,
      form,
      valid,
      activeTab,
      tabs,
      subtitleKey,
      emailDomainItems,
      ipItems,
      countryItems,
      tldItems,
      localDescription,
      recognizedCountries,
      countryEnabled,
      tldEnabled,
      tldOptions,
      tldsLoading,
      emailDomainFieldLabel,
      ipFieldLabel,
      countryFieldLabel,
      tldFieldLabel,
      emailDomainRules,
      ipRules,
      isEmailOrDomainValid,
      isIpValid,
      isCountryCode,
      invalidTerms,
      errorMessages,
      onTabChange,
      validate,
    };
  },
});
</script>

<style lang="scss" scoped>
:deep(*) {
  .v-input--checkbox .v-messages {
    display: block;
  }
}
:deep(.original-text) {
  .v-btn.toggle-button {
    .v-btn__content {
      text-transform: none !important;
    }
  }
}
</style>
